import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check, AlertCircle } from "lucide-react";
import { gardenServices, HOME_LOCATIONS, OFFER_MARKUP } from "../data/services";
import { useLanguage } from "../i18n/LanguageContext";
import { useBackClose } from "../hooks/use-back-close";
import { Reveal } from "./Reveal";

const GardenCard = ({ service, onBook, index, forceOpen, forceKey }) => {
  const { t, L } = useLanguage();
  const [open, setOpen] = useState(false);
  useBackClose(open, () => setOpen(false));
  const hasPackages = !!service.packages?.length;

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen, forceKey]);

  const bookPkg = (p) =>
    onBook({
      service: service.name,
      category: "Garden & Plant Care",
      items: [{ label: p.label, labelBn: p.labelBn, price: p.price, qty: 1 }],
      total: p.price,
      locationOptions: HOME_LOCATIONS,
    });

  const book = () =>
    onBook({
      service: service.name,
      category: "Garden & Plant Care",
      items: null,
      total: service.price || null,
      locationOptions: HOME_LOCATIONS,
    });

  return (
    <Reveal delay={index * 0.05}>
      <div
        data-testid={`garden-card-${service.id}`}
        className={`rounded-3xl bg-[#FAF9F6] border shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden hover:shadow-[0_16px_40px_rgb(0,0,0,0.07)] transition-shadow duration-300 ${
          forceOpen && open ? "border-[#4A7C59] ring-2 ring-[#4A7C59]/30" : "border-stone-200"
        }`}
      >
        <button
          onClick={() => (open ? window.history.back() : setOpen(true))}
          data-testid={`garden-toggle-${service.id}`}
          className="w-full flex items-center gap-5 p-5 sm:p-6 text-left"
        >
          <img
            src={service.image}
            alt={service.name}
            loading="lazy"
            className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-stone-900">
              {L(service, "name")}
            </h3>
            <p className="mt-1 text-base text-stone-500 leading-relaxed line-clamp-2">
              {L(service, "desc")}
            </p>
            <p className="mt-2 text-base font-bold text-[#EAB308]" data-testid={`garden-price-${service.id}`}>
              {L(service, "priceLabel") || t.priceOnEnquiry}
            </p>
          </div>
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="w-5 h-5 text-stone-400" />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
            >
              <div className="px-5 sm:px-6 pb-6 border-t border-stone-100 bg-white rounded-b-3xl">
                {service.includes?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase mb-2">
                      {t.includes}
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {(L(service, "includes") === service.includes
                        ? service.includes
                        : service.includesBn
                      ).map((inc) => (
                        <li key={inc} className="flex items-start gap-2 text-base text-stone-600">
                          <Check className="w-4 h-4 text-[#4A7C59] shrink-0 mt-0.5" />
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {hasPackages && (
                  <div className="mt-5">
                    <p className="text-xs font-semibold tracking-widest text-stone-400 uppercase mb-2">
                      {t.choosePackage}
                    </p>
                    <div className="space-y-2">
                      {service.packages.map((p, i) => (
                        <div
                          key={p.label}
                          data-testid={`garden-package-${service.id}-${i}`}
                          className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-[#FAF9F6] px-4 py-3"
                        >
                          <span className="text-base font-medium text-stone-800 min-w-0 leading-snug">
                            {L(p, "label")}
                          </span>
                          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                            <span className="text-right">
                              <span className="block text-sm line-through text-stone-400">
                                ₹{(p.price + OFFER_MARKUP.garden).toLocaleString("en-IN")}
                              </span>
                              <span className="block text-base font-bold text-[#EAB308]">
                                ₹{p.price.toLocaleString("en-IN")}
                              </span>
                            </span>
                            <motion.button
                              onClick={() => bookPkg(p)}
                              data-testid={`garden-book-${service.id}-${i}`}
                              whileTap={{ scale: 0.95 }}
                              className="rounded-full bg-[#4A7C59] text-white text-sm font-semibold px-4 py-2 hover:bg-[#3c6749] transition-colors duration-200 whitespace-nowrap"
                            >
                              Book Service
                            </motion.button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {service.extraNote && (
                  <p className="mt-4 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    {L(service, "extraNote")}
                  </p>
                )}

                {!hasPackages && (
                  <div className="mt-5 flex justify-end">
                    <motion.button
                      onClick={book}
                      data-testid={`book-${service.id}`}
                      whileTap={{ scale: 0.96 }}
                      className="rounded-full bg-[#4A7C59] text-white text-sm font-semibold px-6 py-2.5 hover:bg-[#3c6749] transition-colors duration-200"
                    >
                      Book Service
                    </motion.button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
};

export const GardenSection = ({ onBook, forceOpenId, forceKey }) => {
  const { t } = useLanguage();
  const ch = t.chapters.garden;
  return (
    <section id="garden" className="py-20 sm:py-28 bg-white" data-testid="garden-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-8">
        <Reveal className="mb-12 sm:mb-16">
          <div className="flex items-start gap-5 sm:gap-8">
            <span
              className="font-display text-7xl sm:text-8xl lg:text-9xl leading-none font-semibold text-transparent select-none"
              style={{ WebkitTextStroke: "1.5px #4A7C59" }}
            >
              {ch.no}
            </span>
            <div className="max-w-xl pt-2 sm:pt-5">
              <p className="text-xs font-semibold tracking-[0.25em] text-[#4A7C59] mb-3 uppercase">
                {ch.eyebrow}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-stone-900">
                {ch.title}
              </h2>
              <p className="mt-4 text-base text-stone-500 leading-relaxed">{ch.sub}</p>
            </div>
          </div>
        </Reveal>
        <div className="space-y-5">
          {gardenServices.map((s, i) => (
            <GardenCard
              key={s.id}
              service={s}
              onBook={onBook}
              index={i}
              forceOpen={forceOpenId === s.id}
              forceKey={forceKey}
            />
          ))}
        </div>
        <Reveal className="mt-8">
          <p
            className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-2xl px-5 py-4"
            data-testid="garden-extra-note"
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {t.gardenNote}
          </p>
        </Reveal>
      </div>
    </section>
  );
};
